import { useEffect, useRef, useState } from 'react';
import { formatHours } from '../utils/events';
import { XP_DAILY_QUEST, XP_WEEKLY_QUEST } from '../utils/progress';

const MUTATION_WINDOW_MS = 3000;
const TWEEN_MS = 700;
const BANNER_MS = 2600;
const FLOAT_MS = 1350;
const KEYS = ['week', 'ytd', 'xp'];

const easeOutCubic = (t) => 1 - (1 - t) ** 3;
const nfEn = new Intl.NumberFormat('en-GB');
export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Fejringer kun, når tallene ændrer sig kort efter brugerens egen handling (mutationAtRef).
// Første load, ugeskift og baggrundsopdatering sætter bare tallene uden animation.
export function useProgressFx({ targets, progress, mutationAtRef }) {
  const [displayed, setDisplayed] = useState(targets);
  const [fx, setFx] = useState([]);
  const [banner, setBanner] = useState(null);
  const [burstKey, setBurstKey] = useState(0);
  const [pulse, setPulse] = useState({ ring: 0, flash: 0, glow: 0, pop: 0 });

  const prev = useRef(null);
  const shown = useRef(targets);
  const tween = useRef(null);
  const nextId = useRef(0);

  const snapshot = {
    ...targets,
    level: progress.level,
    stage: progress.stage,
    dayDone: progress.dayDone,
    weekDone: progress.weekDone,
  };
  const sig = JSON.stringify(snapshot);

  useEffect(() => {
    const before = prev.current;
    prev.current = snapshot;
    const animate = before && Date.now() - (mutationAtRef.current || 0) < MUTATION_WINDOW_MS;

    const setNow = () => { shown.current = targets; setDisplayed(targets); };
    if (!before) { setNow(); return; }
    if (!animate) {
      clearInterval(tween.current);
      setNow();
      return;
    }

    // Tæl op fra det viste tal til det nye.
    clearInterval(tween.current);
    const from = { ...shown.current };
    const t0 = Date.now();
    tween.current = setInterval(() => {
      const t = Math.min(1, (Date.now() - t0) / TWEEN_MS);
      const e = easeOutCubic(t);
      const cur = {};
      KEYS.forEach((k) => { cur[k] = from[k] + (targets[k] - from[k]) * e; });
      shown.current = cur;
      setDisplayed(cur);
      if (t >= 1) clearInterval(tween.current);
    }, 33);

    const dWeek = targets.week - before.week;
    const dXp = targets.xp - before.xp;
    const addFx = (kind, text) => {
      const id = (nextId.current += 1);
      setFx((list) => [...list, { id, kind, text }]);
      setTimeout(() => setFx((list) => list.filter((f) => f.id !== id)), FLOAT_MS);
    };
    if (dWeek > 0.001) addFx('hours', `+${formatHours(dWeek)}`);
    if (dXp > 0) addFx('xp', `+${nfEn.format(Math.round(dXp))} XP`);
    if (dXp > 0) setPulse((p) => ({ ...p, glow: p.glow + 1 }));

    const newStage = snapshot.stage > before.stage;
    const newLevel = snapshot.level > before.level;
    const weeklyDone = snapshot.weekDone && !before.weekDone;
    const dailyDone = snapshot.dayDone && !before.dayDone;
    if (dailyDone) setPulse((p) => ({ ...p, pop: p.pop + 1 }));

    let next = null;
    if (newStage) next = { title: `New armor · ${progress.stageName}`, sub: `Level ${snapshot.level} reached` };
    else if (weeklyDone) next = { title: 'Weekly quest complete', sub: `+${nfEn.format(XP_WEEKLY_QUEST)} XP · ${formatHours(progress.weekHours)} this week` };
    else if (dailyDone) next = { title: 'Daily quest complete', sub: `+${XP_DAILY_QUEST} XP · ${formatHours(progress.dayHours)} today` };
    else if (newLevel) {
      next = {
        title: `Level ${snapshot.level}`,
        sub: progress.nextStageLevel === null ? 'Max armor reached' : `${progress.nextStageLevel - snapshot.level} levels to ${progress.nextStageName}`,
      };
    }
    if (next) {
      setBanner(next);
      setBurstKey((k) => k + 1);
      setTimeout(() => setBanner((b) => (b === next ? null : b)), BANNER_MS);
      if (newStage) setPulse((p) => ({ ...p, flash: p.flash + 1, ring: p.ring + 1, pop: p.pop + 1 }));
      else if (newLevel) setPulse((p) => ({ ...p, ring: p.ring + 1, pop: p.pop + 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  useEffect(() => () => clearInterval(tween.current), []);

  return { displayed, fx, banner, burstKey, pulse };
}
